import { db } from "../firebase-config";
import { collection, query, where, getDoc, onSnapshot, updateDoc, doc, Timestamp, runTransaction, addDoc } from "firebase/firestore";
import moment from 'moment';

// Snapshot Listener functions
export const timeClockEntriesSnapshotListener = (organizationId, userId, onEntriesUpdate) => {
    const entriesRef = collection(db, `organizations/${organizationId}/time_clock_entries`);
    const q = query(
        entriesRef, 
        where('employee_id', '==', userId),
        // Optionally add a limit if you only need recent entries
        // orderBy('timestamp_in', 'desc'),
        // limit(10)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const entriesData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        onEntriesUpdate({ entries: entriesData });
    }, (error) => {
        console.error("Error listening to time clock entries:", error);
    });

    return unsubscribe;
};

// New function to create a manual time clock entry
export const createManualTimeClockEntry = async (organizationId, userId, managerId, timestampIn, timestampOut, reason) => {
    try {
        const currentTime = Timestamp.now();
        const weekStart = moment(timestampIn).utc().startOf('isoWeek').format('YYYY-MM-DD');

        await runTransaction(db, async (transaction) => {
            const newEntry = {
                employee_id: userId,
                timestamp_in: timestampIn,
                timestamp_out: timestampOut,
                created_at: currentTime,
                created_by: managerId,
                week_start: weekStart,
                updated_at: currentTime,
                entry_type: 'manual',
                reason: reason,
                adjustments: [{
                    type: 'creation',
                    timestamp: currentTime,
                    performed_by: managerId,
                    reason: reason,
                    details: {
                        timestamp_in: timestampIn,
                        timestamp_out: timestampOut
                    }
                }]
            };

            const entryRef = doc(collection(db, 'organizations', organizationId, `time_clock_entries`));
            transaction.set(entryRef, newEntry);

            // Add to adjustments subcollection
            const adjustmentRef = doc(collection(entryRef, 'adjustments'));
            transaction.set(adjustmentRef, {
                action: 'manual-entry',
                user_id: managerId,
                employee_id: userId,
                timestamp: currentTime,
                new_timestamp_in: timestampIn,
                new_timestamp_out: timestampOut,
                reason: reason
            });
        });

        return { success: true, error: null };
    } catch (error) {
        console.error("Manual time clock entry creation failed:", error);
        return { success: false, error: error.message || 'Manual entry creation failed' };
    }
};

// Enhanced clockIn function with better tracking
export const clockIn = async (organizationId, userId, source = 'system') => {
    try {
        const currentTime = Timestamp.now();
        const weekStart = moment(currentTime.toDate()).utc().startOf('isoWeek').format('YYYY-MM-DD');

        await runTransaction(db, async (transaction) => {
            const newEntry = {
                employee_id: userId,
                timestamp_in: currentTime,
                created_at: currentTime,
                week_start: weekStart,
                updated_at: currentTime,
                entry_type: 'punch',
                source: source,
                adjustments: [{
                    type: 'clock-in',
                    timestamp: currentTime,
                    performed_by: userId,
                    source: source
                }]
            };

            const entryRef = doc(collection(db, 'organizations', organizationId, `time_clock_entries`));
            transaction.set(entryRef, newEntry);

            const adjustmentRef = doc(collection(entryRef, 'adjustments'));
            transaction.set(adjustmentRef, {
                action: 'clock-in',
                user_id: userId,
                timestamp: currentTime,
                new_timestamp_in: currentTime,
                source: source
            });
        });

        return { success: true, error: null, message: 'Successfully clocked in' };
    } catch (error) {
        console.error("Clock-in failed:", error);
        return { success: false, error: error.message || 'Clock-in failed' };
    }
};

// Enhanced clockOut function with better tracking
export const clockOut = async (organizationId, userId, entryId, source = 'system') => {
    if (!organizationId || !userId || !entryId) {
        return { success: false, error: 'Required parameters are missing' };
    }

    try {
        const currentTime = Timestamp.now();
        const entryRef = doc(db, 'organizations', organizationId, `time_clock_entries`, entryId);

        await runTransaction(db, async (transaction) => {
            const entrySnap = await transaction.get(entryRef);
            if (!entrySnap.exists()) throw new Error('Entry not found.');

            const existingEntry = entrySnap.data();
            const adjustments = existingEntry.adjustments || [];

            // Add new adjustment to the array
            adjustments.push({
                type: 'clock-out',
                timestamp: currentTime,
                performed_by: userId,
                source: source
            });

            transaction.update(entryRef, {
                timestamp_out: currentTime,
                updated_at: currentTime,
                adjustments: adjustments
            });

            const adjustmentRef = doc(collection(entryRef, 'adjustments'));
            transaction.set(adjustmentRef, {
                action: 'clock-out',
                user_id: userId,
                timestamp: currentTime,
                old_timestamp_out: existingEntry.timestamp_out || null,
                new_timestamp_out: currentTime,
                source: source
            });
        });

        return { success: true, error: null, message: 'Successfully clocked out' };
    } catch (error) {
        console.error("Clock-out failed:", error);
        return { success: false, error: error.message || 'Clock-out failed' };
    }
};

export const updateTimeClockEntry = async (organizationId, entryId, managerId, updates, reason) => {
    try {
        const currentTime = Timestamp.now();
        const entryRef = doc(db, 'organizations', organizationId, `time_clock_entries`, entryId);

        await runTransaction(db, async (transaction) => {
            const entrySnap = await transaction.get(entryRef);
            if (!entrySnap.exists()) throw new Error('Entry not found');

            const existingEntry = entrySnap.data();
            const adjustments = existingEntry.adjustments || [];

            // Create adjustment record
            const adjustment = {
                type: 'edit',
                timestamp: currentTime,
                performed_by: managerId,
                reason: reason,
                changes: {}
            };

            // Update object for the document
            const updateData = {
                updated_at: currentTime,
                updated_by: managerId
            };

            // Handle timestamp_in update
            if (updates.timestamp_in) {
                adjustment.changes.timestamp_in = {
                    old: existingEntry.timestamp_in,
                    new: updates.timestamp_in
                };
                updateData.timestamp_in = updates.timestamp_in;
            }

            // Handle timestamp_out update
            if (updates.timestamp_out) {
                adjustment.changes.timestamp_out = {
                    old: existingEntry.timestamp_out,
                    new: updates.timestamp_out
                };
                updateData.timestamp_out = updates.timestamp_out;
            }

            // Add the new adjustment to the array
            adjustments.push(adjustment);
            updateData.adjustments = adjustments;

            // Update the document
            transaction.update(entryRef, updateData);

            // Add to adjustments subcollection
            const adjustmentRef = doc(collection(entryRef, 'adjustments'));
            transaction.set(adjustmentRef, {
                action: 'edit',
                user_id: managerId,
                timestamp: currentTime,
                old_timestamp_in: existingEntry.timestamp_in,
                new_timestamp_in: updates.timestamp_in || existingEntry.timestamp_in,
                old_timestamp_out: existingEntry.timestamp_out,
                new_timestamp_out: updates.timestamp_out || existingEntry.timestamp_out,
                reason: reason
            });
        });

        return { success: true, error: null, message: 'Successfully updated time clock entry' };
    } catch (error) {
        console.error("Update time clock entry failed:", error);
        return { success: false, error: error.message || 'Update failed' };
    }
};

export const fetchWeeklyTimeClocksSnapshotListener = (organizationId, weekString, onTimeClocksUpdate, onError, onLoading) => {
    const collectionPath = `organizations/${organizationId}/time_clock_entries`;

    const weekStart = moment(weekString).startOf('week');
    const weekEnd = weekStart.clone().endOf('week');

    const q = query(
        collection(db, collectionPath),
        where('timestamp_in', '>=', Timestamp.fromDate(weekStart.toDate())),
        where('timestamp_in', '<=', Timestamp.fromDate(weekEnd.toDate()))
    );

    onLoading(true);

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const timeClocks = [];
        querySnapshot.forEach((doc) => {
            timeClocks.push({ id: doc.id, ...doc.data() });
        });
        onTimeClocksUpdate(timeClocks);
        onLoading(false);
    }, (error) => {
        console.error("Error listening to time clock updates:", error);
        onError(error);
        onLoading(false);
    });

    return unsubscribe;
};

// Add this new function for user-specific weekly time clock entries
export const fetchUserWeeklyTimeClocks = (organizationId, userId, weekString, onTimeClocksUpdate, onError, onLoading) => {
    const collectionPath = `organizations/${organizationId}/time_clock_entries`;

    const weekStart = moment(weekString).startOf('week');
    const weekEnd = weekStart.clone().endOf('week');

    const q = query(
        collection(db, collectionPath),
        where('employee_id', '==', userId),
        where('timestamp_in', '>=', Timestamp.fromDate(weekStart.toDate())),
        where('timestamp_in', '<=', Timestamp.fromDate(weekEnd.toDate()))
    );

    onLoading(true);

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const timeClocks = [];
        querySnapshot.forEach((doc) => {
            timeClocks.push({ id: doc.id, ...doc.data() });
        });
        onTimeClocksUpdate(timeClocks);
        onLoading(false);
    }, (error) => {
        console.error("Error listening to time clock updates:", error);
        onError(error);
        onLoading(false);
    });

    return unsubscribe;
};

export const addTimeClockEntry = async (organizationId, timeClockData) => {
    try {
        const currentTime = Timestamp.now();
        const weekStart = moment(timeClockData.timestamp_in).utc().startOf('isoWeek').format('YYYY-MM-DD');

        await runTransaction(db, async (transaction) => {
            const jsDateIn = timeClockData.timestamp_in;
            const jsDateOut = timeClockData.timestamp_out;

            if (!jsDateIn || isNaN(jsDateIn.getTime()) || (jsDateOut && isNaN(jsDateOut.getTime()))) {
                throw new Error('Invalid timestamp format');
            }

            const newEntry = {
                employee_id: timeClockData.employee_id,
                timestamp_in: currentTime,
                created_at: currentTime,
                created_by: timeClockData.created_by,
                week_start: weekStart,
                updated_at: currentTime,
                entry_type: 'manual',
                source: timeClockData.source || 'manual',
                adjustments: [{
                    type: 'creation',
                    timestamp: currentTime,
                    performed_by: timeClockData.adjustments[0].performed_by,
                    reason: timeClockData.adjustments[0].reason,
                    source: timeClockData.adjustments[0].source || 'manual'
                }]
            };

            // Convert timestamp_in to Firestore timestamp
            newEntry.timestamp_in = Timestamp.fromDate(jsDateIn);

            // Convert timestamp_out to Firestore timestamp if it exists
            if (jsDateOut) {
                newEntry.timestamp_out = Timestamp.fromDate(jsDateOut);
            }

            const entryRef = doc(collection(db, 'organizations', organizationId, 'time_clock_entries'));
            transaction.set(entryRef, newEntry);

            // Add to adjustments subcollection
            const adjustmentRef = doc(collection(entryRef, 'adjustments'));
            transaction.set(adjustmentRef, {
                action: 'creation',
                user_id: timeClockData.adjustments[0].performed_by,
                employee_id: timeClockData.employee_id,
                timestamp: currentTime,
                new_timestamp_in: newEntry.timestamp_in,
                new_timestamp_out: newEntry.timestamp_out || null,
                reason: timeClockData.adjustments[0].reason,
                source: timeClockData.adjustments[0].source || 'manual'
            });
        });

        return { success: true, error: null, message: 'Successfully added time clock entry' };
    } catch (error) {
        console.error("Add time clock entry failed:", error);
        return { success: false, error: error.message || 'Add failed' };
    }
};